/**
 * UserShell — the single shell of the authenticated user area (Phase 3).
 *
 * It owns everything that must not be rebuilt per page: product identity,
 * navigation, theme switch, notifications, account menu, the one and only
 * user-facing <Toaster/>, and the content container. Pages own their own
 * <PageHeader/> and their content, nothing else.
 *
 * It also mounts <WorkspaceProvider>, so moving between routes no longer
 * destroys the workspace state (selection, weights, running analysis, results).
 */
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { Toaster } from "sonner";

import { AccountMenu } from "@/components/AccountMenu";
import { NotificationCenter } from "@/components/NotificationCenter";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";

const NAV = [
  { to: "/", label: "Analisi", testId: "nav-workspace" },
  { to: "/portfolios", label: "I miei portafogli", testId: "nav-portfolios" },
  { to: "/profile", label: "Profilo", testId: "nav-profile" },
];

function ShellNav() {
  return (
    <nav aria-label="Sezioni principali" data-testid="user-nav">
      <ul className="flex items-center gap-1">
        {NAV.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end
              data-testid={item.testId}
              className={({ isActive }) => [
                "inline-flex items-center min-h-10 px-3 rounded-full text-sm",
                "transition-colors duration-fast ease-system",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                isActive
                  ? "bg-surface-subtle text-content font-semibold border border-line"
                  : "text-content-secondary hover:text-content hover:bg-surface-subtle border border-transparent",
              ].join(" ")}
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function UserShell() {
  const { user, isAdmin, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The workspace needs the full canvas; account pages read better narrow.
  const width = pathname.startsWith("/profile")
    ? "max-w-3xl"
    : pathname.startsWith("/portfolios") ? "max-w-6xl" : "max-w-none";

  return (
    <WorkspaceProvider>
      <div className="app-container" data-testid="app-container">
        <Toaster position="top-right" theme={theme} richColors />
        <div className="app-content-wrapper">
          <div className={`w-full mx-auto ${width}`}>
            <header
              data-testid="user-shell-header"
              className="mb-6 sm:mb-8 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-3
                         sm:flex sm:flex-wrap sm:items-center sm:justify-between sm:gap-4"
            >
              <div className="flex items-center gap-3 min-w-0 sm:order-1">
                <NavLink
                  to="/"
                  data-testid="brand-home-link"
                  aria-label="ETF Holdings Consolidator — vai all'analisi"
                  className="flex items-center gap-2 sm:gap-3 min-w-0 min-h-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <span className="w-10 h-10 rounded-lg bg-brand flex items-center justify-center shrink-0">
                    <BarChart3 className="w-5 h-5 text-content-on-brand" aria-hidden="true" />
                  </span>
                  <span className="text-base sm:text-lg md:text-xl font-semibold tracking-tight text-content truncate">
                    ETF Holdings Consolidator
                  </span>
                </NavLink>
              </div>

              {/* Mobile: brand + actions on the first row, navigation below.
                  Desktop (≥640): the historical single row, unchanged. */}
              <div className="col-span-2 row-start-2 min-w-0 sm:order-2 sm:col-span-1 sm:row-auto">
                <ShellNav />
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 row-start-1 col-start-2 sm:row-auto sm:col-auto sm:order-3">
                <ThemeToggle />
                <NotificationCenter />
                <AccountMenu
                  user={user}
                  isAdmin={isAdmin}
                  onOpenProfile={() => navigate("/profile")}
                  onOpenAdmin={() => navigate("/admin")}
                  onLogout={logout}
                />
              </div>
            </header>

            <main data-testid="user-shell-main">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </WorkspaceProvider>
  );
}

export default UserShell;
